import { useId, useMemo, useState } from 'react';
import type { Trade } from '../lib/api';

/**
 * YES price over time.
 *
 * Form: change-over-time, one series → a line. One series means no legend; the
 * heading names it. The y-axis is pinned to 0–100 rather than fitted to the
 * data, because the value is a probability and a market that moved from 48% to
 * 52% should look like it barely moved. Auto-fitting would make that swing fill
 * the panel and read as drama.
 *
 * Colour is categorical slot 1 from the validated palette; the fill beneath is
 * the same hue at low alpha, which carries no independent meaning and is there
 * only to anchor the line to the axis.
 */
export function PriceChart({ trades, height = 180 }: { trades: Trade[]; height?: number }) {
  const gradientId = useId();
  const [hover, setHover] = useState<number | null>(null);

  // The API returns newest first; a time series has to run the other way.
  const points = useMemo(
    () => [...trades].reverse().map((t) => ({ price: t.price, at: new Date(t.created_at).getTime() })),
    [trades],
  );

  if (points.length < 2) {
    return <div className="empty-state">Not enough trades yet to plot a price.</div>;
  }

  const W = 600;
  const H = height;
  const PAD = { top: 8, right: 34, bottom: 20, left: 4 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  const x = (i: number) => PAD.left + (points.length === 1 ? plotW / 2 : (i / (points.length - 1)) * plotW);
  const y = (price: number) => PAD.top + plotH - (price / 100) * plotH;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(2)},${y(p.price).toFixed(2)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(2)},${(PAD.top + plotH).toFixed(2)} L${x(0).toFixed(2)},${(PAD.top + plotH).toFixed(2)} Z`;

  const active = hover === null ? null : points[hover];
  const latest = points[points.length - 1]!;

  return (
    <div className="chart">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label={`YES price history. Currently ${latest.price} percent.`}
        onMouseLeave={() => setHover(null)}
        onMouseMove={(e) => {
          const box = e.currentTarget.getBoundingClientRect();
          const rel = ((e.clientX - box.left) / box.width) * W;
          const i = Math.round(((rel - PAD.left) / plotW) * (points.length - 1));
          setHover(Math.min(points.length - 1, Math.max(0, i)));
        }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--yes)" stopOpacity="0.22" />
            <stop offset="100%" stopColor="var(--yes)" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {[0, 25, 50, 75, 100].map((v) => (
          <g key={v}>
            <line className="grid-line" x1={PAD.left} x2={PAD.left + plotW} y1={y(v)} y2={y(v)} />
            <text className="axis-label" x={PAD.left + plotW + 6} y={y(v) + 4}>
              {v}%
            </text>
          </g>
        ))}

        <path className="area" d={area} fill={`url(#${gradientId})`} />
        <path className="series" d={line} />

        {active && hover !== null && (
          <g>
            <line className="crosshair" x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + plotH} />
            <circle className="dot" cx={x(hover)} cy={y(active.price)} r={4.5} />
          </g>
        )}
        {hover === null && <circle className="dot" cx={x(points.length - 1)} cy={y(latest.price)} r={4.5} />}
      </svg>

      {active && hover !== null && (
        <div
          className="chart-tip"
          style={{
            left: `${(x(hover) / W) * 100}%`,
            top: `${(y(active.price) / H) * 100}%`,
            transform: 'translate(-50%, -140%)',
          }}
        >
          <b>{active.price}%</b>
          <span>{new Date(active.at).toLocaleString()}</span>
        </div>
      )}
    </div>
  );
}
